import React, { useCallback, useContext, useMemo, useState } from 'react';
import Columns from './Columns';
import BoardContext from '../../../../contexts/BoardContext';
import updateBoard from '../../../../api/boards/updateBoard';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import getBaseViewFields from '../../../Grid/getBaseViewFields';
import BaseViewsContext from '../../../../contexts/BaseViewsContext';
import {
  CONTRACT_ON_TARGET_CELL_FIELD,
  CONTRACT_PROGRESS_CELL_FIELD,
  ON_TARGET_CELL_FIELD,
  PROGRESS_CELL_FIELD,
} from '../../../Grid/gridConstants';
import isDefined from '../../../../isDefined';
import isCustomerLaneBoard from '../../../../isCustomerLaneBoard';
import { getSpecialCellField } from '../../../../hooks/useColumnDefs';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import isPerformanceBoard from '../../../../isPerformanceBoard';
import _ from 'lodash';
import Form from '../../../../kingpin/forms/Form';
import FormHeader from '../../../../kingpin/forms/FormHeader';
import FormContent from '../../../../kingpin/forms/FormContent';
import Alert from '../../../../kingpin/Alert';

// TODO: We need to handle performance boards here.
// The special cells were previously configured.
// They should now be derived.
// Possibly performance boards should have their own file for this feature?

const ColumnsContainer = ({
  baseView,
  close,
}: {
  baseView: FleetOps.BaseView;
  close: () => void;
}) => {
  const { accountRef } = useContext(AccountPickerContext);
  const { board } = useContext(BoardContext);
  const [isSizedToFitOverridden, setIsSizedToFitOverridden] = useState<
    boolean | undefined
  >(isPerformanceBoard(board) ? undefined : board.isSizedToFitOverridden);
  if (isPerformanceBoard(board)) {
    const e = new Error();
    e.name = 'Customer Lane Commitments: Performance board found';
    throw e;
  }
  const { columnOrder } = board;
  const newColumns = getBaseViewFields(baseView).filter(
    (c) => !columnOrder.some((cO) => cO.field === c.field),
  );

  const extraColumns = useMemo(() => {
    return isCustomerLaneBoard(board)
      ? [
          PROGRESS_CELL_FIELD,
          CONTRACT_PROGRESS_CELL_FIELD,
          ON_TARGET_CELL_FIELD,
          CONTRACT_ON_TARGET_CELL_FIELD,
        ]
      : isPerformanceBoard(board)
        ? window.emptyArray
        : board.specialCells
          ? board.specialCells.map(getSpecialCellField)
          : window.emptyArray;
  }, [board]);

  const netColumnOrder = useMemo(() => {
    return _.uniqBy(
      [
        ...columnOrder.filter(
          (cO) =>
            getBaseViewFields(baseView).some((c) => c.field === cO.field) ||
            extraColumns.includes(cO.field),
        ),
        ...newColumns.map((c) => ({ field: c.field, visible: false })),
        ...extraColumns
          .map((special) => {
            if (columnOrder.some((o) => o.field === special)) {
              return undefined;
            } else {
              return {
                field: special,
                visible: false,
              };
            }
          })
          .filter(isDefined),
      ],
      'field',
    );
  }, [baseView, columnOrder, extraColumns, newColumns]);

  const onIsSizedToFitChange = useCallback(
    (newValue: boolean) => {
      setIsSizedToFitOverridden(newValue);
      const newBoard = {
        ...board,
        isSizedToFitOverridden: newValue,
      };

      updateBoard(newBoard, accountRef);
    },
    [accountRef, board],
  );

  return (
    <Columns
      board={board}
      columnOrder={netColumnOrder}
      close={close}
      isSizedToFitOverridden={isSizedToFitOverridden}
      onIsSizedToFitChange={onIsSizedToFitChange}
    />
  );
};

const Gate = ({
  close,
  isOpen,
  dataType,
}: {
  close: () => void;
  isOpen: boolean;
  dataType: string;
}) => {
  const { baseViews } = useContext(BaseViewsContext);
  const baseView = baseViews[dataType];

  if (baseView) {
    return (
      <ModalTransition>
        {isOpen && (
          <ModalDialog
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            {isOpen && <ColumnsContainer baseView={baseView} close={close} />}
          </ModalDialog>
        )}
      </ModalTransition>
    );
  } else {
    return (
      <ModalTransition>
        {isOpen && (
          <ModalDialog
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            {isOpen && (
              <Form>
                <FormHeader onClose={close} title={'Columns'} />
                <FormContent>
                  <Alert
                    type={'Info'}
                    body={`This feature is unavailable as the "${dataType}" dataset is not defined`}
                  />
                </FormContent>
              </Form>
            )}
          </ModalDialog>
        )}
      </ModalTransition>
    );
  }
};

export default Gate;
